import fetch from 'isomorphic-fetch'
import config from 'config'
import rootStore from '@vue-storefront/core/store'
import { processURLAddress } from '@vue-storefront/core/helpers'
import { SearchResponse } from '@vue-storefront/core/types/search/SearchResponse'

/**
 * C2 remediation: calls the semantic vuestorefront-api endpoints (`/api/catalog-search/*`)
 * that replace the old raw-ES-DSL-over-`/api/catalog/*` path - the frontend now sends plain
 * params, the backend resolves the per-city index and scopes by partner_code itself. Mirrors
 * quickSearchByQuery's partnerCode/locationCode resolution (core/lib/search.ts) so callers see
 * the exact same "no active partner -> empty result" short-circuit, and returns the same
 * SearchResponse shape so list()'s post-processing (caching, price sync, event emission)
 * doesn't need to know how the data was actually fetched.
 */

const EMPTY_RESPONSE: SearchResponse = { items: [], total: 0, start: 0, perPage: 0, aggregations: null, suggestions: null }

function resolvePartnerCode (partner: string | false): string {
  return partner || rootStore.state['partners'].current_partner_code
}

function resolveLocationCode (): string {
  const location = rootStore.getters['locations/getActiveLocation']
  return location && location.code
}

async function callCatalogSearch (path: string, params: Record<string, any>): Promise<any> {
  const query = Object.keys(params)
    .filter(key => params[key] !== undefined && params[key] !== null && params[key] !== false)
    .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
    .join('&')
  const url = processURLAddress(path) + (query ? '?' + query : '')
  const resp = await fetch(url, {
    method: 'GET',
    headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' }
  })
  return resp.json()
}

// POST variant - only /products needs it (its params - filters array, field lists - don't fit
// cleanly in a query string the way every other endpoint's flat params do).
async function postCatalogSearch (path: string, body: Record<string, any>): Promise<any> {
  const resp = await fetch(processURLAddress(path), {
    method: 'POST',
    headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  })
  return resp.json()
}

export async function lookupProduct ({ sku, id, partner = false }: { sku?: string, id?: number, partner?: string | false }): Promise<SearchResponse> {
  const partnerCode = resolvePartnerCode(partner)
  if (!partnerCode) return EMPTY_RESPONSE
  const locationCode = resolveLocationCode()

  const resp = await callCatalogSearch(config.catalogSearch.productsLookup, { locationCode, partnerCode, sku, id })
  const items = resp.item ? [resp.item] : []
  return { items, total: items.length, start: 0, perPage: 1, aggregations: null, suggestions: null }
}

// `categoryIds` covers ProductRelated.vue's own fallback: when a product has no configured
// product_links (no linked skus), it asks for other products in the same category instead -
// same base filters (visibility/status/stock/partner_code) either way, just a different
// match field, matching prepareRelatedQuery's real (key, value) generic filter.
export async function productsBySkus ({ skus, categoryIds, size, partner = false }: { skus?: string[], categoryIds?: number[], size?: number, partner?: string | false }): Promise<SearchResponse> {
  // ProductRelated.vue's own category_ids fallback has passed a bare number here before (its
  // shop-display-mode branches use a single category id, not an array) - normalize defensively
  // rather than trust every caller to already be an array.
  if (skus !== undefined && !Array.isArray(skus)) skus = [skus]
  if (categoryIds !== undefined && !Array.isArray(categoryIds)) categoryIds = [categoryIds]

  const partnerCode = resolvePartnerCode(partner)
  const hasSkus = skus && skus.length
  const hasCategoryIds = categoryIds && categoryIds.length
  if (!partnerCode || (!hasSkus && !hasCategoryIds)) return EMPTY_RESPONSE
  const locationCode = resolveLocationCode()

  const resp = await callCatalogSearch(config.catalogSearch.productsBySkus, {
    locationCode,
    partnerCode,
    skus: hasSkus ? skus.join(',') : undefined,
    categoryIds: !hasSkus && hasCategoryIds ? categoryIds.join(',') : undefined,
    size
  })
  const items = resp.items || []
  return { items, total: items.length, start: 0, perPage: items.length, aggregations: null, suggestions: null }
}

// Real callers (PartnerView.vue, ProductsSwiper.vue, Grid.vue) only ever filter by
// category_ids - prepareQuery's other path (config-driven generic key/value filters via
// `queryConfig`) has no live caller: config.query.{inspirations,newProducts,bestSellers} are
// unused demo-theme placeholders, not real categories in this deployment. Not migrated here.
// categoryIds is optional (K2-3000's "most ordered" query has no category to filter by, only
// a partner + sort) - omitted entirely rather than sent as an empty string: an empty
// `categoryIds=` query param round-trips through buildCuratedQuery's `''.split(',').map(Number)`
// as `[NaN]`, which is truthy-length and would silently filter out every real product.
export async function curatedProducts ({ categoryIds, start = 0, size = 12, sort = '', partner = false }: { categoryIds?: number[], start?: number, size?: number, sort?: string, partner?: string | false }): Promise<SearchResponse> {
  const partnerCode = resolvePartnerCode(partner)
  if (!partnerCode) return EMPTY_RESPONSE
  const locationCode = resolveLocationCode()

  const resp = await callCatalogSearch(config.catalogSearch.productsCurated, {
    locationCode, partnerCode, categoryIds: categoryIds && categoryIds.length ? categoryIds.join(',') : undefined, from: start, size, sort: sort || undefined
  })
  const items = resp.items || []
  return { items, total: items.length, start, perPage: size, aggregations: null, suggestions: null }
}

// Matches category/actions.ts::list()'s real param surface - checked every real
// dispatch('category/list', ...) call site rather than porting its full generic (key, value)
// signature. Only one caller uses (key, value) at all (product/actions.ts, always
// key:'entity_id' with an array) - the action itself falls back to the old ES-DSL path for
// any other key, since no real caller needs one today.
export async function listCategories ({ parentId, entityIds, level, onlyNotEmpty = false, partner = false }: { parentId?: number, entityIds?: number[], level?: number, onlyNotEmpty?: boolean, partner?: string | false }): Promise<SearchResponse> {
  const partnerCode = resolvePartnerCode(partner)
  if (!partnerCode) return EMPTY_RESPONSE
  const locationCode = resolveLocationCode()

  const resp = await callCatalogSearch(config.catalogSearch.categories, {
    locationCode,
    partnerCode,
    parentId,
    entityIds: entityIds && entityIds.length ? entityIds.join(',') : undefined,
    level,
    onlyNotEmpty: onlyNotEmpty || undefined
  })
  const items = resp.items || []
  return { items, total: items.length, start: 0, perPage: items.length, aggregations: null, suggestions: null }
}

// Translates an already-built SearchQuery (from core/helpers/index.ts's
// baseFilterProductsQuery/buildFilterProductsQuery - the PLP's real query builder) back into
// plain params, instead of changing every calling component (Category.js x3, Sidebar.js,
// CategoryFilters.ts) to build structured params directly. visibility/status/stock.is_in_stock
// are dropped - the new endpoint already applies them unconditionally. Returns null for any
// filter shape this doesn't recognize (not just category_ids/price/a plain eq), so the caller
// can fall back to the old ES-DSL path rather than silently mis-querying on an unexpected shape.
function extractProductsParams (searchQuery): { categoryIds: number[], filters: { attributeCode: string, value: any }[], priceMin?: number, priceMax?: number } | null {
  let categoryIds: number[] = null
  let filters = []
  let priceMin
  let priceMax

  for (const f of searchQuery.getAppliedFilters()) {
    if (f.attribute === 'visibility' || f.attribute === 'status' || f.attribute === 'stock.is_in_stock') {
      continue
    }
    if (f.attribute === 'category_ids') {
      categoryIds = Array.isArray(f.value.in) ? f.value.in : [f.value.in]
      continue
    }
    if (f.attribute === 'price') {
      if (f.value.gte !== undefined) priceMin = f.value.gte
      if (f.value.lte !== undefined) priceMax = f.value.lte
      continue
    }
    if (f.value && f.value.eq !== undefined) {
      filters.push({ attributeCode: f.attribute, value: f.value.eq })
      continue
    }
    return null
  }
  if (categoryIds === null) return null
  return { categoryIds, filters, priceMin, priceMax }
}

export async function productsByCategory ({ searchQuery, start = 0, size = 50, sort = '', includeFields = null, excludeFields = null, partner = false }: { searchQuery: any, start?: number, size?: number, sort?: string, includeFields?: string[], excludeFields?: string[], partner?: string | false }): Promise<SearchResponse | null> {
  const extracted = extractProductsParams(searchQuery)
  if (!extracted) return null // caller falls back to the old ES-DSL path

  const partnerCode = resolvePartnerCode(partner)
  if (!partnerCode) return EMPTY_RESPONSE
  const locationCode = resolveLocationCode()

  const resp = await postCatalogSearch(config.catalogSearch.products, {
    locationCode,
    partnerCode,
    categoryId: extracted.categoryIds[0], // subtree root - the endpoint re-resolves the full subtree itself
    filters: extracted.filters,
    priceMin: extracted.priceMin,
    priceMax: extracted.priceMax,
    sort: sort || undefined,
    page: Math.floor(start / size) + 1,
    pageSize: size,
    includeFields: includeFields || undefined,
    excludeFields: excludeFields || undefined
  })
  return { items: resp.items || [], total: resp.total || 0, start, perPage: size, aggregations: null, suggestions: null }
}

export async function searchProducts ({ text, start = 0, size = 50, sort = '', partner = false }: { text: string, start?: number, size?: number, sort?: string, partner?: string | false }): Promise<SearchResponse> {
  const partnerCode = resolvePartnerCode(partner)
  if (!partnerCode) return EMPTY_RESPONSE
  const locationCode = resolveLocationCode()
  const page = Math.floor(start / size) + 1

  const resp = await callCatalogSearch(config.catalogSearch.productsSearch, { locationCode, partnerCode, text, page, pageSize: size, sort: sort || undefined })
  return { items: resp.items || [], total: resp.total || 0, start, perPage: size, aggregations: null, suggestions: null }
}
